<template>
  <div
    :class="bem({ isActive })"
    :dato-id="props.post.id"
  >
    <DatoCmsImage
      :class="bem('image')"
      :data="props.post.thumbnail.responsiveImage"
      :object-position="`${props.post.thumbnail.focalPoint.x * 100}% ${
        props.post.thumbnail.focalPoint.y * 100
      }%`"
      layout="fill"
    />

    <div
      :class="bem('toggle-target')"
      @click="handleToggleClick"
    />

    <div
      v-if="props.post.sku && props.post.productTitle"
      :class="bem('product')"
    >
      <div :class="bem('product-wrapper')">
        <DynamicImg
          v-if="props.post.sku"
          :sku="props.post.sku"
          :width="400"
          :height="400"
          bg="transparent"
          :view-angle="2"
          :lazy-load="true"
        />
        <VisionLink
          :is-button="props.doesChangeCustomisedProduct"
          :to="calculatedLink"
          :class="bem('product-link')"
          @click="handleLinkClick"
        >
          <VisionHeading
            tag="h6"
            size="h5"
            :class="bem('product-link-text')"
            alignment="center"
          >
            Shop Design
          </VisionHeading>
          <div :class="bem('icon-container')">
            <VisionIcon
              :class="bem('icon')"
              icon="chevron"
              size="stretch"
            />
          </div>
        </VisionLink>
      </div>
    </div>
    <div
      v-if="props.post.productTitle && props.showTitle"
      :class="bem('product-title-wrapper')"
    >
      <VisionHeading
        tag="h6"
        size="h3"
        :class="bem('product-title')"
      >
        {{ props.post.productTitle }}
      </VisionHeading>
    </div>
  </div>
</template>

<script setup lang="ts">
import { Image as DatoCmsImage } from 'vue-datocms'
const { $track } = useNuxtApp()

const emit = defineEmits(['active-post-id', 'change-customised-product'])
const bem = useBEM('InstagramCard')

interface InstagramCardProps {
  post: any
  isActive: boolean
  position: number
  trackingId: string
  showTitle: boolean
  doesChangeCustomisedProduct: boolean
}

const props = withDefaults(defineProps<InstagramCardProps>(), {
  post: () => {},
  isActive: false,
  position: 0,
  trackingId: '',
  showTitle: true,
  doesChangeCustomisedProduct: false
})

const calculatedLink = computed(() => {
  if (props.doesChangeCustomisedProduct) return null
  return `/shop/redirect?sku=${props.post.sku}`
})

const handleToggleClick = () => {
  if (props.isActive) {
    emit('active-post-id', null)
    return
  }
  emit('active-post-id', props.post.id)
}

async function handleLinkClick() {
  await $track.global.linkClicked({
    linkText: props.post.productTitle,
    interactionElement: `${props.post.productTitle} - ${props.post.id}`,
    interactionSection: props.trackingId,
    interactionCategory: 'Instagram Shop',
    position: props.position + 1
  }, 'Clicked Instagram Card');

  if (props.doesChangeCustomisedProduct) {
    emit('change-customised-product', props.post.sku)
    window.scrollTo({
      top: 0,
      behavior: 'smooth',
    })
  }
}
</script>

<style lang="postcss">
.InstagramCard {
  @apply relative
    overflow-hidden
    will-change-transform
    w-full h-full;
  container: instagramCard / inline-size;
}

.InstagramCard__toggle-target {
  @apply absolute inset-0 z-[2] cursor-pointer;
}

.InstagramCard__product {
  @apply absolute right-0 bottom-0
    m-2 z-[1];
}

.InstagramCard__product-wrapper {
  @apply relative
    bg-white bg-opacity-[0.68]
    backdrop-blur
    overflow-hidden rounded-md
    w-20 h-20
    transition-all duration-300
    will-change-transform;
}

.InstagramCard__product-link {
  @apply absolute bottom-0 inset-x-0
    flex items-center justify-center
    p-3
    space-x-1
    bg-white
    translate-y-full
    transition-transform duration-200;

  @screen xxs {
    @apply py-4;
  }
}

.InstagramCard__product-link-text {
  @apply whitespace-nowrap;
}

.InstagramCard__icon-container {
  @apply relative
    shrink-0
    w-[14px] h-[14px]
    flex items-center justify-center
    rounded-full;
  background: var(--gradient-default);

  @screen md {
    @apply w-[17px] h-[17px];
  }
}

.InstagramCard__icon {
  @apply absolute
    w-2 h-2
    left-[3px] md:left-[5px];
}

.InstagramCard__product-title-wrapper {
  @apply absolute top-0 p-4 inset-0
  opacity-0 -translate-y-1/2
  transition-all duration-300;
  background: linear-gradient(to bottom, rgba(0, 0, 0, 0.24), rgba(0, 0, 0, 0));
}

.InstagramCard__product-title {
  @apply text-white text-md;
}

/**
States
--is-active
*/
.InstagramCard--is-active {
  @apply cursor-default;

  .InstagramCard__product {
    @apply z-[4];
  }

  .InstagramCard__product-wrapper {
    @apply w-32;
    height: calc(theme('width.32') + 48px);
  }

  .InstagramCard__product-link {
    @apply translate-y-0
      delay-100;

    &:hover {
      .InstagramCard__product-link-text {
        @apply t-gradient;
      }
    }
  }

  .InstagramCard__product-title-wrapper {
    @apply translate-y-0 delay-150 opacity-100;
  }
}

/**
  Container Queries
*/
@container instagramCard (min-width: 280px) {
  .InstagramCard__product-title {
    @apply text-2xl;
  }

  .InstagramCard--is-active {
    .InstagramCard__product-wrapper {
      @apply w-40;
      height: calc(theme('size.40') + 48px);
    }
  }
}
</style>
